import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import html2canvas from "html2canvas";
import "./tokenlyglobal.css";

const Trackii = () => {
    const navigate = useNavigate();
    const [dashboardData, setDashboardData] = useState(null);
    const [tokenData, setTokenData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [switching, setSwitching] = useState(false);
    const [error, setError] = useState("");
    const dashboardRef = useRef(null);

    const getAuthToken = () => localStorage.getItem("tokenly_token") || "";

    const fetchAllData = useCallback(async () => {
        try {
            const authToken = getAuthToken();
            if (!authToken) {
                navigate("/login");
                return;
            }

            const headers = {
                "Content-Type": "application/json",
                Authorization: `Bearer ${authToken}`,
            };

            const [dashRes, tokenRes] = await Promise.all([
                fetch("https://tokenly.biswaas.com/api/user/userDashboardData", { method: "POST", headers, body: JSON.stringify({}) }),
                fetch("https://tokenly.biswaas.com/api/doctor/checkToken", { method: "POST", headers, body: JSON.stringify({}) })
            ]);

            const dashResult = await dashRes.json();
            const tokenResult = await tokenRes.json();

            if (dashResult.status && dashResult.data) {
                setDashboardData(dashResult.data);
            }

            if (tokenResult.status && tokenResult.data) {
                setTokenData(Array.isArray(tokenResult.data) ? tokenResult.data[0] : tokenResult.data);
            } else {
                setTokenData(null);
            }

            setError("");
        } catch (err) {
            console.error("Fetch Error:", err);
        } finally {
            setLoading(false);
        }
    }, [navigate]);

    // setDefaultToken logic from mobile
    const setDefaultToken = async (tokenId) => {
        setSwitching(true);
        try {
            const authToken = getAuthToken();
            const response = await fetch("https://tokenly.biswaas.com/api/user/setDefaultToken", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${authToken}`
                },
                body: JSON.stringify({ TokenId: tokenId })
            });
            const result = await response.json();
            if (result.status) {
                await fetchAllData();
            }
        } catch (err) {
            console.error("Error setting default token:", err);
        } finally {
            setSwitching(false);
        }
    };

    // Simple screenshot capture and download function
    const captureScreenshot = async () => {
        if (!dashboardRef.current) return;

        try {
            const canvas = await html2canvas(dashboardRef.current, {
                backgroundColor: '#ffffff',
                scale: 2,
                logging: false,
                useCORS: true,
                allowTaint: false,
                foreignObjectRendering: false,
                imageTimeout: 15000,
                width: dashboardRef.current.offsetWidth,
                height: dashboardRef.current.offsetHeight
            });

            canvas.toBlob((blob) => {
                if (!blob) {
                    console.error('Failed to capture screenshot');
                    return;
                }

                const fileName = `tokenly-dashboard-${moment().format('YYYY-MM-DD-HH-mm')}.png`;
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = fileName;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(url);
            }, 'image/png');
        } catch (error) {
            console.error('Screenshot failed:', error);
        }
    };

    useEffect(() => {
        // Scroll to top when component mounts
        window.scrollTo(0, 0);
        fetchAllData();
        const interval = setInterval(fetchAllData, 10000); // Changed to 10 seconds
        return () => clearInterval(interval);
    }, [fetchAllData]);

    if (loading && !tokenData && !dashboardData) {
        return (
            <div className="d-flex flex-column justify-content-center align-items-center" style={{ minHeight: "80vh" }}>
                <div className="spinner-border text-primary mb-3" role="status"></div>
                <p className="text-center text-muted small">Synchronizing with dashboard...</p>
            </div>
        );
    }

    const doctor = tokenData?.doctor || {};
    const otherTokens = dashboardData?.otherToken || [];
    const showNext = (doctor.status != 2 && tokenData?.currentToken == 1) || tokenData?.currentTokenStatus === 0;

    return (
        <div className="trackii-container py-4" style={{ marginTop: "85px", background: "#f8fafc", minHeight: "calc(100vh - 85px)" }}>
            <div className="container">
                <div className="row justify-content-center">
                    <div className="col-lg-6 col-md-8 px-2">

                        {/* Live Status Badge */}
                        <div className="text-center mb-3">
                            <div className="d-inline-flex align-items-center bg-white px-3 py-1 rounded-pill shadow-sm border">
                                <span className={tokenData?.status === 2 ? "dot-red" : "dot-green"}></span>
                                <span className="fw-bold" style={{ fontSize: '11px', color: tokenData?.status === 2 ? '#ef4444' : '#10b981' }}>
                                    {tokenData?.status === 2 ? "TOKEN SKIPED" : "LIVE DASHBOARD"}
                                </span>
                            </div>
                        </div>

                        <div className="card border-0 shadow-lg overflow-hidden position-relative" ref={dashboardRef} style={{ borderRadius: '24px', backgroundColor: '#fff' }}>
                            {switching && (
                                <div className="position-absolute top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center bg-white bg-opacity-75" style={{ zIndex: 10 }}>
                                    <div className="spinner-border text-primary"></div>
                                </div>
                            )}

                            <div className="card-body p-0">

                                {/* CASE 1: TREATED (Status 1) - EXACT MOBILE LOGIC */}
                                {tokenData?.status === 1 && (
                                    <div className="p-5 text-center">
                                        <p className="text-muted mb-2 small fw-medium">Your last consultation was with</p>
                                        <h5 className="fw-bold text-dark mb-1">Dr. {doctor.fullname}</h5>
                                        <p className="text-muted small mb-4">on {moment(tokenData.updated_at).format('DD/MM/YYYY hh:mm a')}.</p>
                                        <button className="btn btn-primary w-100 rounded-pill py-2 fw-bold shadow-sm"
                                            onClick={() => navigate(`/doctor/${tokenData.doctor_id}`)}>
                                            GENERATE NEW TOKEN
                                        </button>
                                    </div>
                                )}

                                {/* CASE 2: ACTIVE (Status 0 or 4) */}
                                {(tokenData?.status === 0 || tokenData?.status === 4) && tokenData?.isToday && (
                                    <>
                                        <div className="p-4 bg-white border-bottom text-center">
                                            <div className="row g-0">
                                                <div className="col-6 border-end">
                                                    <p className="text-muted mb-2 fw-bold" style={{ fontSize: '10px' }}>MY TOKEN NO</p>
                                                    <div className="mx-auto d-flex align-items-center justify-content-center"
                                                        style={{ width: '70px', height: '70px', background: '#3b82f6', borderRadius: '50%', color: 'white', fontSize: '26px', fontWeight: '900' }}>
                                                        {tokenData.token_no}
                                                    </div>
                                                </div>
                                                <div className="col-6">
                                                    <p className="text-muted mb-2 fw-bold" style={{ fontSize: '10px' }}>
                                                        {showNext ? "NEXT TOKEN NO." : "CURRENT TOKEN NO."}
                                                    </p>
                                                    <div className="mx-auto d-flex align-items-center justify-content-center"
                                                        style={{ width: '70px', height: '70px', background: '#10b981', borderRadius: '50%', color: 'white', fontSize: '26px', fontWeight: '900' }}>
                                                        {showNext ? "N/A" : tokenData.currentToken}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="p-4 bg-light-subtle">
                                            <div className="mb-3">
                                                <div className="d-flex align-items-center mb-2">
                                                    <div className="fw-bold text-muted small" style={{ width: '100px' }}>Time Slot</div>
                                                    <div className="fw-bold text-dark small">: {tokenData.shift_time || "N/A"}</div>
                                                </div>
                                                <div className="d-flex align-items-center mb-2">
                                                    <div className="fw-bold text-muted small" style={{ width: '100px' }}>Doctor's Name</div>
                                                    <div className="fw-bold text-dark small text-truncate pe-2">
                                                        : {doctor.name_title || 'Dr.'} {doctor.fullname}
                                                        <span className={doctor.status === 2 ? "text-success ms-1" : "text-danger ms-1"} style={{ fontSize: '10px' }}>
                                                            ({doctor.status === 2 ? "Available" : "Ideal"})
                                                        </span>
                                                    </div>
                                                </div>
                                                {doctor.appointment_type == 0 && (
                                                    <div className="d-flex mb-2">
                                                        <div className="fw-bold text-muted small" style={{ width: '100px' }}>Expected Time</div>
                                                        <div className="fw-bold text-dark small flex-grow-1">
                                                            : {tokenData.doctor?.current_token != null ? tokenData.expectedTime || 'N/A' : 'Check back after shift starts'}
                                                            <div className="text-muted fw-normal" style={{ fontSize: '10px' }}>Expected Time may be early or later</div>
                                                        </div>
                                                    </div>
                                                )}
                                                <div className="d-flex align-items-center">
                                                    <div className="fw-bold text-muted small" style={{ width: '100px' }}>Direction</div>
                                                    <div className="fw-bold text-dark small" style={{ cursor: 'pointer' }}
                                                        onClick={() => window.open(`http://maps.google.com/maps?saddr=My+Location&daddr=${doctor.lat},${doctor.lng}`, '_blank')}>
                                                        : <span className="text-primary text-decoration-underline">View On Map</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </>
                                )}

                                {/* CASE 3: SKIPPED (Status 2) - EXACT MOBILE LOGIC */}
                                {tokenData?.status === 2 && (
                                    <div className="p-5 text-center">
                                        <h5 className="fw-bold text-dark mb-3">
                                            Your Token has been skiped with Dr. {doctor.fullname}
                                        </h5>
                                        <div className="p-3 bg-light rounded-4 mb-4">
                                            <p className="text-muted small mb-1 fw-bold">Token No. : {tokenData.token_no}</p>
                                        </div>
                                        <button className="btn btn-primary w-100 rounded-pill py-2 fw-bold shadow-sm"
                                            onClick={() => navigate(`/doctor/${tokenData.doctor_id}`)}>
                                            REGENERATE TOKEN FOR TODAY
                                        </button>
                                    </div>
                                )}

                                {/* OTHER TOKENS - GRID UI WITH SWITCHING FUNCTION */}
                                {otherTokens.length > 0 && (
                                    <div className="p-4 pt-3 border-top bg-light-subtle">
                                        <div className="text-muted mb-3 fw-bold px-1" style={{ fontSize: '10px', letterSpacing: '0.8px' }}>OTHER TOKENS</div>
                                        <div className="row g-2 px-1">
                                            {otherTokens.map((other, idx) => (
                                                <div key={idx} className="col-6 col-md-3">
                                                    <div className="p-2 d-flex align-items-center bg-white border rounded-3 shadow-sm h-100 transition-hover"
                                                        onClick={() => setDefaultToken(other.id)}
                                                        style={{ borderLeft: '3px solid #10b981', minHeight: '45px', cursor: 'pointer' }}>
                                                        <div className="bg-success text-white rounded-circle d-flex align-items-center justify-content-center fw-bold me-2"
                                                            style={{ width: '24px', height: '24px', fontSize: '10px', flexShrink: 0 }}>
                                                            {other.token_no}
                                                        </div>
                                                        <div className="overflow-hidden">
                                                            <div className="text-dark fw-bold text-truncate" style={{ fontSize: '9px' }}>{other.doctor?.fullname}</div>
                                                            <div className="text-muted text-truncate" style={{ fontSize: '8px' }}>{other.doctor?.clinic_name || "N/A"}</div>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* COMPLIMENTARY TOKENS ALERT */}
                        {dashboardData?.free_token > 0 && (
                            <div className="mt-4 p-3 rounded-4 bg-white shadow-sm border text-center">
                                <p className="text-danger fw-bold mb-0 small" style={{ letterSpacing: '0.3px' }}>
                                    <i className="fa-solid fa-gift me-2"></i>
                                    You have {dashboardData.free_token} complimentary tokens available.
                                </p>
                            </div>
                        )}

                        {/* DOWNLOAD SCREENSHOT BUTTON - Only show when token is available */}
                        {/* {tokenData && (
                            <div className="mt-4 text-center">
                                <button
                                    className="btn btn-primary rounded-pill px-4 py-2 shadow-sm"
                                    onClick={captureScreenshot}
                                    style={{ background: '#059669', border: 'none' }}
                                >
                                    <i className="fa-solid fa-download me-2"></i>
                                    Download And Share
                                </button>
                            </div>
                        )} */}
                    </div>
                </div>
            </div>

            <style dangerouslySetInnerHTML={{
                __html: `
                .dot-green { width: 8px; height: 8px; background-color: #10b981; border-radius: 50%; border: 2px solid #fff; margin-right: 8px; }
                .dot-red { width: 8px; height: 8px; background-color: #ef4444; border-radius: 50%; border: 2px solid #fff; margin-right: 8px; }
                .bg-light-subtle { background-color: #fafbfc; }
                .transition-hover:hover { transform: translateY(-2px); transition: 0.2s; background: #f0fdf4 !important; }
            `}} />
        </div>
    );
};

export default Trackii;
